/**
 * Player accounts — Supabase Auth (Google) + a public profile row. (§9.1 follow-on)
 *
 * Owner asks (verbatim intent): "use supabase to store data", "add a google
 * sign in", "player Id and profile selection". See docs/ACCOUNTS-SPEC.md for
 * the full plan and the SQL schema this module talks to.
 *
 * ── Guests keep working — sign-in unlocks, it is never a wall ───────────────
 *
 * VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are absent in local dev unless a
 * contributor opts in, and even in production a player can decline to sign
 * in. Every export here is written to degrade to a clean "signed out, no
 * client" state rather than throw, so nothing upstream needs an `if
 * (accountsEnabled)` guard before touching the menu's PROFILE tab.
 *
 * ── Identity split from the local profile ────────────────────────────────────
 *
 * profile-store.ts's `PlayerProfile.id` is a random per-browser id used as
 * the online transport's player id — it exists independent of any account
 * and must keep existing (guests still need a stable id within a session).
 * This module's account id is the Supabase auth user id (uuid), used only to
 * attach a *persistent* profile (username/avatar/rating) across devices. The
 * two ids are never conflated: menu code reads the account username when
 * signed in and the local profile name otherwise (see arena-menu.ts).
 */

import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';

export interface AccountProfile {
  id: string;
  username: string;
  avatar: number;
  rating: number;
}

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Null when the env vars are unset — the one branch every caller must
 * tolerate. Created once at module load: Supabase's client is cheap and
 * stateless to construct, and a singleton means every screen shares the same
 * in-memory session cache instead of each re-deriving it from storage.
 */
export const supabase: SupabaseClient | null =
  url && anonKey ? createClient(url, anonKey) : null;

export function accountsEnabled(): boolean {
  return supabase !== null;
}

export async function getSession(): Promise<Session | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/**
 * The JWT to hand the lobby at join time so the match is recorded against this
 * account (docs/ACCOUNTS-SPEC.md phase 2). Undefined — not null — because it
 * feeds an optional request field that should be OMITTED when absent.
 *
 * getSession() refreshes an expired token transparently, so this is always
 * either fresh or absent; callers never have to think about expiry.
 */
export async function getAccessToken(): Promise<string | undefined> {
  const session = await getSession();
  return session?.access_token ?? undefined;
}

/**
 * Fires immediately with the current state, then on every sign-in/out.
 * Returns a no-op unsubscribe when accounts are disabled so callers can
 * `const stop = onAuthChange(...)` unconditionally.
 */
export function onAuthChange(cb: (session: Session | null) => void): () => void {
  if (!supabase) return () => {};
  const { data } = supabase.auth.onAuthStateChange((_event, session) => cb(session));
  return () => data.subscription.unsubscribe();
}

/**
 * Redirects to Google and back to the current page (including its query
 * string, so a deep-linked room invite or ?join= survives the round trip).
 * There is no return value — the page navigates away.
 */
export async function signInWithGoogle(): Promise<void> {
  if (!supabase) return;
  await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.href },
  });
}

export async function signOut(): Promise<void> {
  if (!supabase) return;
  await supabase.auth.signOut();
}

/** One row of the ranked leaderboard (phase 3). */
export interface LeaderboardEntry {
  username: string;
  rating: number;
  avatar: number;
}

/**
 * Top players by rating, highest first. Empty when accounts are disabled or
 * on any error — no separate error state for the leaderboard screen to
 * handle, same "degrade to nothing rather than throw" shape as
 * getMatchHistory(). A plain Supabase read (RLS already makes `profiles`
 * public-read), so no sign-in is required to view it.
 */
export async function getLeaderboard(limit = 20): Promise<LeaderboardEntry[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('profiles')
    .select('username, rating, avatar')
    .order('rating', { ascending: false })
    .limit(limit);
  if (error || !data) return [];
  return data as LeaderboardEntry[];
}

/** Null when signed out, accounts are disabled, or the profile row doesn't exist yet. */
export async function getProfile(): Promise<AccountProfile | null> {
  if (!supabase) return null;
  const session = await getSession();
  if (!session) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, avatar, rating')
    .eq('id', session.user.id)
    .maybeSingle();
  if (error || !data) return null;
  return data as AccountProfile;
}

/** One finished match as the PROFILE tab lists it (phase 2). */
export interface MatchHistoryEntry {
  matchId: number;
  mode: string;
  /** Goals for / against from THIS player's point of view, not A/B. */
  scored: number;
  conceded: number;
  result: 'W' | 'L' | 'D';
  playedAt: string;
}

/**
 * The signed-in player's recent matches, newest first. Empty for guests, for a
 * signed-in player who has not finished a match yet, and on any error — the
 * PROFILE tab treats all three the same way ("no matches yet"), so there is
 * nothing for a caller to distinguish.
 *
 * Reads are allowed by the schema's public-read RLS policies; the WRITES come
 * only from the game server's service key (see server/src/match-report.ts).
 */
export async function getMatchHistory(limit = 10): Promise<MatchHistoryEntry[]> {
  if (!supabase) return [];
  const session = await getSession();
  if (!session) return [];
  const { data, error } = await supabase
    .from('match_players')
    .select('team, match_id, matches!inner(mode, team_a_score, team_b_score, played_at)')
    .eq('profile_id', session.user.id)
    // match_id is a monotonic identity column, so ordering by it is ordering by
    // recency — and unlike ordering on the embedded `matches.played_at` it
    // needs no referencedTable option, which has moved around between
    // supabase-js versions.
    .order('match_id', { ascending: false })
    .limit(limit);
  if (error || !data) return [];

  const rows = data as unknown as Array<{
    team: string;
    match_id: number;
    // PostgREST returns an embedded to-one relation as an object, but the
    // generated types often widen it to an array — tolerate both rather than
    // trusting either.
    matches:
      | { mode: string; team_a_score: number; team_b_score: number; played_at: string }
      | Array<{ mode: string; team_a_score: number; team_b_score: number; played_at: string }>;
  }>;

  const out: MatchHistoryEntry[] = [];
  for (const row of rows) {
    const m = Array.isArray(row.matches) ? row.matches[0] : row.matches;
    if (!m) continue;
    const scored = row.team === 'A' ? m.team_a_score : m.team_b_score;
    const conceded = row.team === 'A' ? m.team_b_score : m.team_a_score;
    out.push({
      matchId: row.match_id,
      mode: m.mode,
      scored,
      conceded,
      result: scored > conceded ? 'W' : scored < conceded ? 'L' : 'D',
      playedAt: m.played_at,
    });
  }
  return out;
}

export type SaveProfileResult =
  | { ok: true; profile: AccountProfile }
  | { ok: false; error: 'not-signed-in' | 'username-taken' | 'unknown' };

/**
 * Upsert-by-id: the first save creates the row, later saves edit it in
 * place. `username` collisions surface as a distinct error (Postgres unique
 * violation, code 23505) so the caller can say "that name is taken" instead
 * of a generic failure.
 */
export async function saveProfile(username: string, avatar: number): Promise<SaveProfileResult> {
  if (!supabase) return { ok: false, error: 'not-signed-in' };
  const session = await getSession();
  if (!session) return { ok: false, error: 'not-signed-in' };
  const { data, error } = await supabase
    .from('profiles')
    .upsert({ id: session.user.id, username, avatar }, { onConflict: 'id' })
    .select('id, username, avatar, rating')
    .single();
  if (error) {
    if (error.code === '23505') return { ok: false, error: 'username-taken' };
    return { ok: false, error: 'unknown' };
  }
  return { ok: true, profile: data as AccountProfile };
}
